import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useGetPageContextDescriptionsQuery } from '@backrs_ui/grow-ui/src/redux/pageContextDescriptions/pageContextDescriptions.api';
import {
  selectIsSharedSessionActive,
  selectSharedSessionOwnerFirstName,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';

import type {
  AgentContext,
  AgentContextEntry,
  AgentContextResource,
  SharedContentContext,
} from './types';

type AgentContextValue = Readonly<{
  registerEntry: (id: string, entry: AgentContextEntry) => void;
  unregisterEntry: (id: string) => void;
  setSharedContext: (ctx: SharedContentContext | null) => void;
  getContext: () => AgentContext;
}>;

const AgentContextCtx = createContext<AgentContextValue | null>(null);

export const useAgentContext = () => useContext(AgentContextCtx);

const PageContextRegistrar = ({
  currentRoutePath,
  currentRouteSubRoute,
}: {
  currentRoutePath?: string;
  currentRouteSubRoute?: string;
}) => {
  const ctx = useAgentContext();
  const id = useId();
  const params = useParams();
  const { data: descriptions } = useGetPageContextDescriptionsQuery(undefined);

  const hasSubRoute = currentRouteSubRoute && params[currentRouteSubRoute];
  const effectivePath = hasSubRoute
    ? `${currentRoutePath}/:${currentRouteSubRoute}`
    : currentRoutePath;

  const match = descriptions?.find((d) => d.route_pattern === effectivePath);

  useEffect(() => {
    if (match) ctx?.registerEntry(id, { kind: 'page', description: match.description });
    else ctx?.unregisterEntry(id);
  });

  useEffect(() => () => ctx?.unregisterEntry(id), [ctx, id]);

  return null;
};

const SharedContextRegistrar = () => {
  const ctx = useAgentContext();
  const isSharedSessionActive = useAppSelector(selectIsSharedSessionActive);
  const ownerFirstName = useAppSelector(selectSharedSessionOwnerFirstName);

  useEffect(() => {
    if (!ctx) return;

    if (!isSharedSessionActive || !ownerFirstName) {
      ctx.setSharedContext(null);

      return;
    }

    ctx.setSharedContext({
      owner_first_name: ownerFirstName,
    });

    return () => {
      ctx.setSharedContext(null);
    };
  }, [ctx, isSharedSessionActive, ownerFirstName]);

  return null;
};

export const AgentContextProvider = ({
  children,
  currentRoutePath,
  currentRouteSubRoute,
}: {
  children: ReactNode;
  currentRoutePath?: string;
  currentRouteSubRoute?: string;
}) => {
  const entriesRef = useRef(new Map<string, AgentContextEntry>());
  const sharedContextRef = useRef<SharedContentContext | null>(null);
  const location = useLocation();
  // Read through a ref so navigating doesn't change `value`, which would re-run every
  // registrar's effects — dropping their entries for the rest of that commit.
  const locationRef = useRef(location);

  locationRef.current = location;

  const value: AgentContextValue = useMemo(
    () => ({
      registerEntry: (id, entry) => {
        entriesRef.current.set(id, entry);
      },
      unregisterEntry: (id) => {
        entriesRef.current.delete(id);
      },
      setSharedContext: (ctx) => {
        sharedContextRef.current = ctx;
      },
      getContext: () => ({
        route: locationRef.current.pathname,
        page_title: document.title,
        resources: Array.from(entriesRef.current.entries()).map(
          ([id, entry]): AgentContextResource => ({ id, ...entry }),
        ),
        ...(sharedContextRef.current ? { shared_context: sharedContextRef.current } : {}),
      }),
    }),
    [],
  );

  return (
    <AgentContextCtx.Provider value={value}>
      <PageContextRegistrar
        currentRoutePath={currentRoutePath}
        currentRouteSubRoute={currentRouteSubRoute}
      />
      <SharedContextRegistrar />
      {children}
    </AgentContextCtx.Provider>
  );
};
